import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { useDispatch, useSelector } from 'react-redux';
import { Table, TableBody, Typography, TableContainer, TableHead, TableRow, Paper, IconButton, Stack, Pagination, Box, Chip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { DeleteConfirmationModal } from '../../../../../utils/modal';
import SearchInput from '../../../../../utils/searchInput';
import { deleteLabTestRequest, getLabTestRequest, resetDeleteLabTest } from '../../../../../redux/modules/laboratory/labTest/slice';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

let deleteItemId;
let limit = 10;
let pageCount = 0;

export default function List({ setItemData, toggleList, setCurrentPage, currentPage }) {
    const dispatch = useDispatch();
    let { getRes, deleteRes } = useSelector(state => state.labTest);
    let { currentBranch } = useSelector(state => state.branch);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(1);

    useEffect(() => {
        if (!currentBranch) { return }
        dispatch(getLabTestRequest({ page: currentPage, limit, searchQuery }));
    }, [dispatch, currentBranch, currentPage, searchQuery]);

    useEffect(() => {
        if (deleteRes) {
            if (deleteRes?.success) {
                dispatch(getLabTestRequest({ page: currentPage, limit, searchQuery }));
            }
            toast(deleteRes?.message);
            dispatch(resetDeleteLabTest());
        }
    }, [dispatch, deleteRes, searchQuery, currentPage]);

    if (getRes?.dataCount && getRes?.dataCount > limit) {
        pageCount = Math.ceil(getRes?.dataCount / limit);
    }

    const resultTypeLabel = (type) => {
        const labels = { numeric: 'Numeric', text: 'Text', coded: 'Coded', multiline: 'Multiline', calculated: 'Calculated' };
        return labels[type] || type;
    };

    return (
        <>
            <Stack mb={3} direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
                <SearchInput setSearchQuery={(searchValue) => { setSearchQuery(searchValue); setPage(1) }} />
            </Stack>
            <Box sx={{ overflowX: { xs: 'auto', md: 'visible' }, mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 } }}>
                <TableContainer component={Paper} className='tableShadow'>
                    <Table sx={{ minWidth: { xs: 400, md: 700 } }} aria-label="test catalog table" className='commonTable'>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Code</StyledTableCell>
                                <StyledTableCell>Name</StyledTableCell>
                                <StyledTableCell>Department</StyledTableCell>
                                <StyledTableCell>Result Type</StyledTableCell>
                                <StyledTableCell>Unit</StyledTableCell>
                                <StyledTableCell>Specimen</StyledTableCell>
                                <StyledTableCell>Price</StyledTableCell>
                                <StyledTableCell align="right">Action</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {getRes?.data?.length > 0 ?
                                getRes?.data?.map(elem => (
                                    <StyledTableRow key={elem._id}>
                                        <StyledTableCell>{elem?.testCode}</StyledTableCell>
                                        <StyledTableCell>{elem?.name}</StyledTableCell>
                                        <StyledTableCell>{elem?.departmentId?.name || '-'}</StyledTableCell>
                                        <StyledTableCell>
                                            <Chip label={resultTypeLabel(elem?.resultType)} size="small" variant="outlined" />
                                        </StyledTableCell>
                                        <StyledTableCell>{elem?.unit || '-'}</StyledTableCell>
                                        <StyledTableCell>{elem?.specimenTypeId?.name || '-'}</StyledTableCell>
                                        <StyledTableCell>{elem?.price ? `$${elem.price}` : '-'}</StyledTableCell>
                                        <StyledTableCell align="right">
                                            <IconButton color="primary" onClick={() => { setItemData(elem); toggleList() }}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton color="error" onClick={() => { deleteItemId = elem._id; setIsModalOpen(true) }}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))
                                :
                                <StyledTableRow>
                                    <StyledTableCell colSpan={8} align="left"><Typography>There is no data to show</Typography></StyledTableCell>
                                </StyledTableRow>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            {(getRes?.dataCount > limit) && <Pagination count={pageCount} page={currentPage} onChange={(event, value) => { setPage(value); setCurrentPage(value) }} />}
            <DeleteConfirmationModal open={isModalOpen} onClose={() => setIsModalOpen(false)} onConfirm={() => { dispatch(deleteLabTestRequest(deleteItemId)); setIsModalOpen(false) }} />
        </>
    );
}
